import { useRef } from 'react';

import { mascaraDaRolagem, useRolagemHorizontal } from '@ds/core';
import { AppointmentCard } from '@ds/data';
import { horaDe, horaFracionaria } from '@/comuns/formato';
import type { Agendamento } from '@/data/tipos';

/** Uma coluna da grade: um dia, ou uma sala quando a visão é por recurso. */
export interface ColunaDaGrade {
    chave: string;
    titulo: string;
    /** Segunda linha do cabeçalho: o dia da semana, quando a coluna é um dia. */
    subtitulo?: string;
    destacada?: boolean;
    atenuada?: boolean;
}

export interface GradeDaAgendaProps {
    colunas: ColunaDaGrade[];
    /** Agendamentos já filtrados, e a que coluna cada um pertence. */
    agendamentos: Agendamento[];
    colunaDo: (agendamento: Agendamento) => string | undefined;
    horaInicial: number;
    horaFinal: number;
    onAbrir: (agendamento: Agendamento) => void;
}

const ALTURA_DA_HORA = 82;

/**
 * Largura mínima de uma coluna. Abaixo disto o cartão do agendamento não mostra
 * nem o horário: a 375 px a semana dava 31 px por dia, e "09:00" e "sáb" saíam
 * cortados. Com o piso, o celular vê três dias e rola para os outros; a partir
 * do tablet a semana inteira continua cabendo.
 */
const LARGURA_MINIMA_DA_COLUNA = 84;

/**
 * Grade de horários.
 *
 * A posição sai do instante do agendamento, não de um índice de linha: com
 * índice, 14:30 era desenhado às 14:00 e uma consulta de 40 minutos ocupava uma
 * hora inteira — a agenda mostrava um encaixe que não existia.
 */
export function GradeDaAgenda({ colunas, agendamentos, colunaDo, horaInicial, horaFinal, onAbrir }: GradeDaAgendaProps) {
    const horas = Array.from({ length: horaFinal - horaInicial }, (_, i) => horaInicial + i);
    const gridTemplateColumns = `64px repeat(${colunas.length},minmax(${LARGURA_MINIMA_DA_COLUNA}px,1fr))`;
    const rolavel = useRef<HTMLDivElement | null>(null);
    const rolagem = useRolagemHorizontal(rolavel, colunas.length);

    return (
        <div style={{ flex: 1, minHeight: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Um rolador só, nos dois eixos: cabeçalho e corpo andam juntos de lado.
                O cabeçalho gruda no topo e as horas grudam à esquerda. Só a borda
                direita esmaece — a esquerda é a coluna de horas, que fica. */}
            {/* Semana sem agendamento não tem nada clicável dentro, e a área rolável ficava
                inalcançável pelo teclado (axe: scrollable-region-focusable). Só então ela entra
                no Tab: com agendamentos, são os botões deles que levam o teclado pela grade. */}
            <div
                ref={rolavel} data-rolagem="horizontal"
                role="region" aria-label="Grade da agenda" tabIndex={agendamentos.length === 0 ? 0 : undefined}
                style={{ flex: 1, minHeight: 0, overflow: 'auto', ...mascaraDaRolagem({ antes: false, depois: rolagem.depois }) }}
            >
                <div style={{ display: 'grid', gridTemplateColumns, borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 3 }}>
                    <div style={{ position: 'sticky', left: 0, background: 'var(--surface)' }} />
                    {colunas.map((c) => (
                        <div key={c.chave} style={{ height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, borderLeft: '1px solid var(--border-subtle)' }}>
                            <span
                                style={{
                                    minWidth: 26, height: 26, padding: '0 6px', borderRadius: c.titulo.length > 2 ? 'var(--radius-pill)' : '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                    background: c.destacada ? 'var(--burgundy-700)' : 'transparent',
                                    color: c.destacada ? '#fff' : 'var(--text-primary)',
                                }}
                            >
                                {c.titulo}
                            </span>
                            {c.subtitulo && <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 500 }}>{c.subtitulo}</span>}
                        </div>
                    ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns, position: 'relative' }}>
                    <div style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)' }}>
                        {horas.map((h) => (
                            <div key={h} style={{ height: ALTURA_DA_HORA, paddingRight: 10, textAlign: 'right', fontSize: 11.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', transform: 'translateY(-7px)' }}>
                                {String(h).padStart(2, '0')}:00
                            </div>
                        ))}
                    </div>

                    {colunas.map((coluna) => (
                        <div key={coluna.chave} style={{ borderLeft: '1px solid var(--border-subtle)', position: 'relative' }}>
                            {horas.map((h) => (
                                <div key={h} style={{ height: ALTURA_DA_HORA, borderBottom: '1px solid var(--border-subtle)', background: coluna.atenuada ? 'var(--surface-secondary)' : 'transparent' }} />
                            ))}

                            {agendamentos.filter((a) => colunaDo(a) === coluna.chave).map((a) => {
                                const inicio = horaFracionaria(a.inicioEm);
                                const topo = (inicio - horaInicial) * ALTURA_DA_HORA + 3;
                                const altura = (a.duracaoMin / 60) * ALTURA_DA_HORA - 7;
                                return (
                                    <div key={a.id} style={{ position: 'absolute', left: 4, right: 4, top: topo, height: Math.max(altura, 26) }}>
                                        <AppointmentCard
                                            time={horaDe(a.inicioEm)} patient={a.paciente} procedure={a.procedimento}
                                            professional={a.profissional} room={a.sala} status={a.status}
                                            lead={a.vinculo?.tipo === 'lead'}
                                            compact={a.duracaoMin < 60}
                                            onClick={() => onAbrir(a)}
                                            style={{ height: '100%', alignItems: 'flex-start' }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
